import { makeAutoObservable, runInAction } from 'mobx';

interface Note {
  id: string;
  title: string;
  content: string;
  createdAt: Date;
  modifiedAt: Date;
}

class NotepadStore {
  private _notes: Note[] = [];
  private _activeNoteId: string | null = null;
  private _isLoading = false;
  private _autoSaveTimeout: NodeJS.Timeout | null = null;

  constructor() {
    makeAutoObservable(this);
    this.loadNotes();
  }

  get notes(): Note[] {
    return this._notes;
  }

  get activeNote(): Note | null {
    if (!this._activeNoteId) return null;
    return this._notes.find(note => note.id === this._activeNoteId) || null;
  }

  get activeNoteId(): string | null {
    return this._activeNoteId;
  }

  get isLoading(): boolean {
    return this._isLoading;
  }

  get sortedNotes(): Note[] {
    return [...this._notes].sort((a, b) => 
      new Date(b.modifiedAt).getTime() - new Date(a.modifiedAt).getTime()
    );
  }

  createNote(title: string = 'Untitled Note'): Note {
    const note: Note = {
      id: this.generateId(),
      title: title.trim() || 'Untitled Note',
      content: '',
      createdAt: new Date(),
      modifiedAt: new Date()
    };

    this._notes.push(note);
    this._activeNoteId = note.id;
    this.saveNotes();
    
    return note;
  }

  deleteNote(noteId: string): boolean {
    const noteIndex = this._notes.findIndex(note => note.id === noteId);
    if (noteIndex === -1) {
      console.warn('Note not found:', noteId);
      return false;
    }

    this._notes.splice(noteIndex, 1);
    
    if (this._activeNoteId === noteId) {
      this._activeNoteId = this._notes.length > 0 ? this._notes[0].id : null;
    }
    
    this.saveNotes();
    return true;
  }

  selectNote(noteId: string): boolean {
    const note = this._notes.find(note => note.id === noteId);
    if (!note) {
      console.warn('Note not found:', noteId);
      return false;
    }

    this._activeNoteId = noteId;
    return true;
  }

  updateNoteTitle(noteId: string, title: string): boolean {
    const note = this._notes.find(note => note.id === noteId);
    if (!note) {
      console.warn('Note not found:', noteId);
      return false;
    }

    note.title = title.trim() || 'Untitled Note';
    note.modifiedAt = new Date();
    
    this.scheduleAutoSave();
    return true;
  }

  updateNoteContent(noteId: string, content: string): boolean {
    const note = this._notes.find(note => note.id === noteId);
    if (!note) {
      console.warn('Note not found:', noteId);
      return false;
    }

    note.content = content;
    note.modifiedAt = new Date();
    
    this.scheduleAutoSave();
    return true;
  }

  duplicateNote(noteId: string): Note | null {
    const originalNote = this._notes.find(note => note.id === noteId);
    if (!originalNote) {
      console.warn('Note not found:', noteId);
      return null;
    }

    const duplicatedNote: Note = {
      id: this.generateId(),
      title: `${originalNote.title} (Copy)`,
      content: originalNote.content,
      createdAt: new Date(),
      modifiedAt: new Date()
    };

    this._notes.push(duplicatedNote);
    this._activeNoteId = duplicatedNote.id;
    this.saveNotes();
    
    return duplicatedNote;
  }

  searchNotes(query: string): Note[] {
    if (!query.trim()) return this.sortedNotes;
    
    const searchTerm = query.toLowerCase();
    return this._notes.filter(note => 
      note.title.toLowerCase().includes(searchTerm) ||
      note.content.toLowerCase().includes(searchTerm)
    ).sort((a, b) => 
      new Date(b.modifiedAt).getTime() - new Date(a.modifiedAt).getTime()
    );
  }

  exportNote(noteId: string): string | null {
    const note = this._notes.find(note => note.id === noteId);
    if (!note) {
      console.warn('Note not found:', noteId);
      return null;
    }

    const exportData = {
      title: note.title,
      content: note.content,
      createdAt: note.createdAt.toISOString(),
      modifiedAt: note.modifiedAt.toISOString()
    };

    return JSON.stringify(exportData, null, 2);
  }

  private scheduleAutoSave(): void {
    if (this._autoSaveTimeout) {
      clearTimeout(this._autoSaveTimeout);
    }
    
    this._autoSaveTimeout = setTimeout(() => {
      this.saveNotes();
    }, 1000); // Auto-save after 1 second of inactivity
  }

  private saveNotes(): void {
    try {
      const notesData = {
        notes: this._notes,
        activeNoteId: this._activeNoteId
      };
      localStorage.setItem('general-tools-notepad', JSON.stringify(notesData));
    } catch (error) {
      console.error('Failed to save notes:', error);
    }
  }

  private async loadNotes(): Promise<void> {
    try {
      runInAction(() => {
        this._isLoading = true;
      });

      const savedData = localStorage.getItem('general-tools-notepad');
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        
        runInAction(() => {
          this._notes = parsedData.notes.map((note: any) => ({
            ...note,
            createdAt: new Date(note.createdAt),
            modifiedAt: new Date(note.modifiedAt)
          }));
          this._activeNoteId = parsedData.activeNoteId;
        });
      }

      // Create a default note if none exist
      if (this._notes.length === 0) {
        this.createNote('Welcome to Notepad');
        if (this.activeNote) {
          this.updateNoteContent(this.activeNote.id, 
            'Welcome to the General Tools Notepad!\n\n' +
            'This is a simple text editor with auto-save functionality.\n\n' +
            'Features:\n' +
            '• Create, edit, and delete notes\n' +
            '• Auto-save after 1 second of inactivity\n' +
            '• Search through your notes\n' +
            '• Duplicate notes\n' +
            '• Export notes as JSON\n\n' +
            'Start typing to create your first note!'
          );
        }
      }
    } catch (error) {
      console.error('Failed to load notes:', error);
    } finally {
      runInAction(() => {
        this._isLoading = false;
      });
    }
  }

  private generateId(): string {
    return `note-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}

export const notepadStore = new NotepadStore();
